import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft } from "lucide-react";
import { CalendarBuilder } from "@/components/CalendarBuilder";
import { TemplateIcon } from "@/lib/eventTypes";

const CURRENCIES = ["MXN", "USD", "COP", "ARS", "CLP", "PEN", "GTQ", "EUR"];

export default function CreateEvent() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedTpl, setSelectedTpl] = useState(null);

  // Common
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [location, setLocation] = useState("");

  // Potluck slots
  const [slots, setSlots] = useState([]);
  // Calendar
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarSlots, setCalendarSlots] = useState([]);
  const [calSettings, setCalSettings] = useState({
    date_format: "dd/mm/yyyy",
    time_format: "12h",
    visibility: "public",
    field_email: "optional",
    field_phone: "optional",
  });
  // Form
  const [formFields, setFormFields] = useState([]);
  // Goal (gift/fundraiser)
  const [goal, setGoal] = useState({ target_amount: 0, currency: "MXN", subject: "" });
  // Ticket types
  const [ticketTypes, setTicketTypes] = useState([]);
  // Dues
  const [duesConfig, setDuesConfig] = useState({ amount: 0, currency: "MXN", deadline: "", notes: "", allow_partial: false });
  // Store
  const [storeProducts, setStoreProducts] = useState([]);
  // Waiver
  const [waiverConfig, setWaiverConfig] = useState({ waiver_text: "", require_parent_signature: false });

  const [saving, setSaving] = useState(false);

  const etype = selectedTpl?.event_type || "potluck";

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      setTemplates(data.templates || []);
      setGroups(data.groups || []);
    }).catch(() => {});
  }, []);

  const chooseTemplate = (tpl) => {
    setSelectedTpl(tpl);
    if (tpl.event_type === "potluck" || !tpl.event_type) {
      setSlots((tpl.slots || []).map((s) => ({ ...s })));
    }
    setStep(2);
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Ponle un título a tu evento");

    const payload = {
      title,
      description,
      category: selectedTpl?.id || "personalizado",
      event_type: etype,
      event_date: eventDate,
      location,
    };

    if (etype === "potluck") {
      if (slots.length === 0) return toast.error("Agrega al menos un espacio");
      if (slots.some((s) => !s.title.trim())) return toast.error("Cada espacio necesita un título");
      payload.slots = slots.map((s) => ({
        title: s.title, description: s.description || "",
        quantity: parseInt(s.quantity, 10) || 1,
        slot_type: s.slot_type || "item", when: s.when || "",
      }));
    } else if (etype === "calendar") {
      if (calendarSlots.length === 0) return toast.error("Genera al menos un horario");
      payload.calendar_settings = calSettings;
      payload.calendar_slots = calendarSlots.map((s) => ({ date: s.date, start_time: s.start_time, duration: s.duration }));
    } else if (etype === "form") {
      if (formFields.length === 0) return toast.error("Agrega al menos un campo");
      if (formFields.some((f) => !f.label.trim())) return toast.error("Cada campo necesita una etiqueta");
      payload.form_fields = formFields.map((f) => ({
        label: f.label, type: f.type, required: !!f.required, placeholder: f.placeholder || "",
        options: f.options || [],
      }));
    } else if (etype === "gift" || etype === "fundraiser") {
      const amt = parseFloat(goal.target_amount);
      if (!amt || amt <= 0) return toast.error("Ingresa una meta válida");
      payload.goal = { target_amount: amt, currency: goal.currency, subject: goal.subject || "" };
    } else if (etype === "tickets") {
      if (ticketTypes.length === 0) return toast.error("Agrega al menos un tipo de boleto");
      payload.ticket_types = ticketTypes.map((t) => ({
        name: t.name, price: parseFloat(t.price) || 0, currency: t.currency,
        quantity: parseInt(t.quantity, 10) || 1, description: t.description || "",
      }));
    } else if (etype === "dues") {
      const amt = parseFloat(duesConfig.amount);
      if (!amt || amt <= 0) return toast.error("Ingresa el monto de la cuota");
      payload.dues_config = { ...duesConfig, amount: amt };
    } else if (etype === "store") {
      if (storeProducts.length === 0) return toast.error("Agrega al menos un producto");
      payload.store_products = storeProducts.map((p) => ({
        name: p.name, price: parseFloat(p.price) || 0, currency: p.currency,
        quantity: parseInt(p.quantity, 10) || 0, description: p.description || "", image_url: p.image_url || "",
      }));
    } else if (etype === "waiver") {
      if (!waiverConfig.waiver_text.trim()) return toast.error("Escribe el texto del consentimiento");
      payload.waiver_config = waiverConfig;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/events", payload);
      toast.success("¡Evento creado!");
      nav(`/eventos/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        {step === 1 && (
          <div>
            <span className="text-xs uppercase tracking-[0.24em] text-coral">Paso 1 de 2</span>
            <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 font-light">
              ¿Qué necesitas organizar?
            </h1>
            <p className="mt-3 text-ink-500">Elige la plantilla más cercana a lo que quieres hacer.</p>

            <div className="mt-10 space-y-10">
              {groups.map((g) => {
                const list = templates.filter((t) => (t.group || "otros") === g.id);
                if (list.length === 0) return null;
                return (
                  <div key={g.id}>
                    <h2 className="font-heading text-xl text-ink-900 mb-4">{g.name}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {list.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => chooseTemplate(t)}
                          className="text-left bg-white border border-line rounded-2xl p-5 hover:border-coral hover:-translate-y-1 shadow-sm hover:shadow-md transition-transform duration-200 ease-out"
                          data-testid={`template-${t.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 shrink-0 rounded-full bg-cream-100 flex items-center justify-center text-coral">
                              <TemplateIcon name={t.icon} size={18} />
                            </div>
                            <div>
                              <h3 className="font-heading text-lg text-ink-900 leading-tight">{t.name}</h3>
                              <p className="mt-1 text-sm text-ink-500 leading-relaxed">{t.description}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <button
              onClick={() => setStep(1)}
              className="text-ink-500 hover:text-coral flex items-center gap-1 text-sm mb-6"
              data-testid="back-to-templates"
            >
              <ChevronLeft size={16} strokeWidth={1.75} /> Cambiar plantilla
            </button>
            <span className="text-xs uppercase tracking-[0.24em] text-coral">Paso 2 de 2</span>
            <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 font-light">
              Detalles del evento
            </h1>

            <div className="mt-10 space-y-6">
              <BasicFields
                title={title} setTitle={setTitle}
                description={description} setDescription={setDescription}
                eventDate={eventDate} setEventDate={setEventDate}
                location={location} setLocation={setLocation}
                etype={etype}
              />

              {etype === "potluck" && (
                <PotluckSlotsEditor slots={slots} setSlots={setSlots} />
              )}
              {etype === "calendar" && (
                <>
                  <div className="pt-6">
                    <h2 className="font-heading text-2xl text-ink-900 mb-4">Días y horarios</h2>
                    <CalendarBuilder
                      selectedDates={selectedDates} onDatesChange={setSelectedDates}
                      slots={calendarSlots} onSlotsChange={setCalendarSlots}
                      timeFormat={calSettings.time_format}
                    />
                  </div>
                  <CalendarSettingsEditor value={calSettings} onChange={setCalSettings} />
                </>
              )}
              {etype === "form" && (
                <FormFieldsEditor fields={formFields} setFields={setFormFields} />
              )}
              {(etype === "gift" || etype === "fundraiser") && (
                <GoalEditor value={goal} onChange={setGoal} etype={etype} />
              )}
              {etype === "tickets" && (
                <TicketTypesEditor items={ticketTypes} setItems={setTicketTypes} />
              )}
              {etype === "dues" && (
                <DuesEditor value={duesConfig} onChange={setDuesConfig} />
              )}
              {etype === "store" && (
                <StoreProductsEditor items={storeProducts} setItems={setStoreProducts} />
              )}
              {etype === "waiver" && (
                <WaiverEditor value={waiverConfig} onChange={setWaiverConfig} />
              )}

              <div className="pt-8 flex justify-end">
                <Button
                  onClick={submit}
                  disabled={saving}
                  className="rounded-full bg-coral hover:bg-coral-hover text-white px-8 h-12"
                  data-testid="save-event-btn"
                >
                  {saving ? "Creando…" : "Crear evento"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============== SUB-COMPONENTS ==============

function BasicFields({ title, setTitle, description, setDescription, eventDate, setEventDate, location, setLocation, etype }) {
  const showDate = ["potluck", "tickets", "waiver", "form", "fundraiser", "gift"].includes(etype);
  return (
    <>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Título</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl border-line h-12" data-testid="event-title-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {showDate && (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Fecha</Label>
            <Input value={eventDate} onChange={(e) => setEventDate(e.target.value)} placeholder="Sábado 20 de diciembre, 7pm" className="rounded-xl border-line h-12" data-testid="event-date-input" />
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Lugar</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ubicación · Zoom · Online" className="rounded-xl border-line h-12" data-testid="event-location-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Descripción</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl border-line min-h-[100px]" data-testid="event-description-input" />
      </div>
    </>
  );
}

function PotluckSlotsEditor({ slots, setSlots }) {
  const addSlot = () => setSlots([...slots, { title: "", quantity: 1, slot_type: "item", when: "" }]);
  const upd = (i, f, v) => setSlots(slots.map((s, idx) => idx === i ? { ...s, [f]: v } : s));
  const rm = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl text-ink-900">Espacios</h2>
        <Button variant="outline" onClick={addSlot} className="rounded-full border-line hover:bg-cream-100" data-testid="add-slot-btn">
          <Plus size={16} strokeWidth={1.75} className="mr-1" /> Agregar
        </Button>
      </div>
      <div className="space-y-3">
        {slots.map((s, idx) => (
          <div key={idx} className="bg-white border border-line rounded-2xl p-5 grid grid-cols-12 gap-3 items-end" data-testid={`slot-row-${idx}`}>
            <div className="col-span-12 md:col-span-5 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Qué / rol</Label>
              <Input value={s.title} onChange={(e) => upd(idx, "title", e.target.value)} placeholder="Ej: Postre" className="rounded-xl border-line h-11" data-testid={`slot-title-${idx}`} />
            </div>
            <div className="col-span-6 md:col-span-4 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Detalle</Label>
              <Input value={s.when} onChange={(e) => upd(idx, "when", e.target.value)} placeholder="Opcional" className="rounded-xl border-line h-11" data-testid={`slot-when-${idx}`} />
            </div>
            <div className="col-span-4 md:col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cupo</Label>
              <Input type="number" min={1} value={s.quantity} onChange={(e) => upd(idx, "quantity", e.target.value)} className="rounded-xl border-line h-11" data-testid={`slot-qty-${idx}`} />
            </div>
            <div className="col-span-2 md:col-span-1 flex justify-end">
              <button onClick={() => rm(idx)} className="text-ink-300 hover:text-coral h-11 flex items-center" aria-label="Quitar" data-testid={`slot-remove-${idx}`}>
                <Trash2 size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        ))}
        {slots.length === 0 && <p className="text-ink-500 text-sm">Sin espacios aún. Toca &ldquo;Agregar&rdquo; para empezar.</p>}
      </div>
    </div>
  );
}

function CalendarSettingsEditor({ value, onChange }) {
  return (
    <div className="pt-6">
      <h2 className="font-heading text-2xl text-ink-900 mb-4">Ajustes</h2>
      <div className="bg-white border border-line rounded-2xl p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-ink-500">Formato de fecha</Label>
            <Select value={value.date_format} onValueChange={(v) => onChange({ ...value, date_format: v })}>
              <SelectTrigger className="rounded-xl border-line h-10 mt-1.5" data-testid="setting-date-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dd/mm/yyyy">DD/MM/AAAA (México)</SelectItem>
                <SelectItem value="mm/dd/yyyy">MM/DD/AAAA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-ink-500">Formato de hora</Label>
            <Select value={value.time_format} onValueChange={(v) => onChange({ ...value, time_format: v })}>
              <SelectTrigger className="rounded-xl border-line h-10 mt-1.5" data-testid="setting-time-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="12h">12 horas (6:00 pm)</SelectItem>
                <SelectItem value="24h">24 horas (18:00)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500 mb-2 block">Visibilidad de los nombres</Label>
          <RadioGroup value={value.visibility} onValueChange={(v) => onChange({ ...value, visibility: v })} className="space-y-2">
            <label className="flex items-start gap-3 border border-line rounded-xl p-3 cursor-pointer hover:bg-cream-50">
              <RadioGroupItem value="public" data-testid="visibility-public" />
              <span className="text-sm font-medium text-ink-900">Todos ven quién se apuntó</span>
            </label>
            <label className="flex items-start gap-3 border border-line rounded-xl p-3 cursor-pointer hover:bg-cream-50">
              <RadioGroupItem value="private" data-testid="visibility-private" />
              <span className="text-sm font-medium text-ink-900">Solo el organizador</span>
            </label>
          </RadioGroup>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500 mb-2 block">Campos al reservar (Nombre siempre es requerido)</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldChoice label="Correo" value={value.field_email} onChange={(v) => onChange({ ...value, field_email: v })} testid="setting-field-email" />
            <FieldChoice label="Teléfono" value={value.field_phone} onChange={(v) => onChange({ ...value, field_phone: v })} testid="setting-field-phone" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldChoice({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] text-ink-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="required">Requerido</SelectItem>
          <SelectItem value="optional">Opcional</SelectItem>
          <SelectItem value="hidden">No pedir</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function FormFieldsEditor({ fields, setFields }) {
  const add = (type = "text") => setFields([...fields, { label: "", type, required: false, placeholder: "", options: [] }]);
  const upd = (i, f, v) => setFields(fields.map((x, idx) => idx === i ? { ...x, [f]: v } : x));
  const rm = (i) => setFields(fields.filter((_, idx) => idx !== i));
  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl text-ink-900">Campos del formulario</h2>
        <Button variant="outline" onClick={() => add("text")} className="rounded-full border-line hover:bg-cream-100" data-testid="add-field-btn">
          <Plus size={16} strokeWidth={1.75} className="mr-1" /> Agregar campo
        </Button>
      </div>
      <div className="space-y-3">
        {fields.map((f, idx) => (
          <div key={idx} className="bg-white border border-line rounded-2xl p-4 space-y-3" data-testid={`field-row-${idx}`}>
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 md:col-span-5">
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">Etiqueta</Label>
                <Input value={f.label} onChange={(e) => upd(idx, "label", e.target.value)} className="rounded-xl border-line h-10 mt-1" data-testid={`field-label-${idx}`} />
              </div>
              <div className="col-span-6 md:col-span-3">
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">Tipo</Label>
                <Select value={f.type} onValueChange={(v) => upd(idx, "type", v)}>
                  <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid={`field-type-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto corto</SelectItem>
                    <SelectItem value="textarea">Texto largo</SelectItem>
                    <SelectItem value="email">Correo</SelectItem>
                    <SelectItem value="tel">Teléfono</SelectItem>
                    <SelectItem value="number">Número</SelectItem>
                    <SelectItem value="date">Fecha</SelectItem>
                    <SelectItem value="select">Selección</SelectItem>
                    <SelectItem value="radio">Opción única</SelectItem>
                    <SelectItem value="checkbox">Casilla</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-4 md:col-span-3 flex items-center gap-2 mt-6">
                <input type="checkbox" checked={f.required} onChange={(e) => upd(idx, "required", e.target.checked)} className="rounded border-line" data-testid={`field-required-${idx}`} />
                <Label className="text-sm text-ink-700">Requerido</Label>
              </div>
              <div className="col-span-2 md:col-span-1 flex justify-end">
                <button onClick={() => rm(idx)} className="text-ink-300 hover:text-coral h-10 flex items-center" aria-label="Quitar" data-testid={`field-remove-${idx}`}>
                  <Trash2 size={16} strokeWidth={1.75} />
                </button>
              </div>
            </div>
            {(f.type === "select" || f.type === "radio") && (
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">Opciones (una por línea)</Label>
                <Textarea
                  value={(f.options || []).join("\n")}
                  onChange={(e) => upd(idx, "options", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                  className="rounded-xl border-line min-h-[70px] mt-1"
                  data-testid={`field-options-${idx}`}
                />
              </div>
            )}
          </div>
        ))}
        {fields.length === 0 && <p className="text-ink-500 text-sm">Sin campos aún.</p>}
      </div>
    </div>
  );
}

function GoalEditor({ value, onChange, etype }) {
  return (
    <div className="pt-4">
      <h2 className="font-heading text-2xl text-ink-900 mb-4">
        {etype === "gift" ? "Meta y destinatario" : "Meta y causa"}
      </h2>
      <div className="bg-white border border-line rounded-2xl p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Meta</Label>
          <Input type="number" min="0" step="0.01" value={value.target_amount} onChange={(e) => onChange({ ...value, target_amount: e.target.value })} className="rounded-xl border-line h-11 mt-1" data-testid="goal-amount" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Moneda</Label>
          <Select value={value.currency} onValueChange={(v) => onChange({ ...value, currency: v })}>
            <SelectTrigger className="rounded-xl border-line h-11 mt-1" data-testid="goal-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">{etype === "gift" ? "Para (destinatario)" : "Causa"}</Label>
          <Input value={value.subject} onChange={(e) => onChange({ ...value, subject: e.target.value })} placeholder={etype === "gift" ? "Ej: Ana por su cumpleaños" : "Ej: Reconstrucción escuela rural"} className="rounded-xl border-line h-11 mt-1" data-testid="goal-subject" />
        </div>
      </div>
    </div>
  );
}

function TicketTypesEditor({ items, setItems }) {
  const add = () => setItems([...items, { name: "", price: 0, currency: "MXN", quantity: 100, description: "" }]);
  const upd = (i, f, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [f]: v } : x));
  const rm = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl text-ink-900">Tipos de boleto</h2>
        <Button variant="outline" onClick={add} className="rounded-full border-line hover:bg-cream-100" data-testid="add-ticket-type-btn">
          <Plus size={16} strokeWidth={1.75} className="mr-1" /> Agregar tipo
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((t, idx) => (
          <div key={idx} className="bg-white border border-line rounded-2xl p-4 grid grid-cols-12 gap-3 items-end" data-testid={`ticket-row-${idx}`}>
            <div className="col-span-12 md:col-span-4">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Nombre</Label>
              <Input value={t.name} onChange={(e) => upd(idx, "name", e.target.value)} placeholder="General / VIP" className="rounded-xl border-line h-10 mt-1" data-testid={`ticket-name-${idx}`} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Precio</Label>
              <Input type="number" min="0" value={t.price} onChange={(e) => upd(idx, "price", e.target.value)} className="rounded-xl border-line h-10 mt-1" data-testid={`ticket-price-${idx}`} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Moneda</Label>
              <Select value={t.currency} onValueChange={(v) => upd(idx, "currency", v)}>
                <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid={`ticket-currency-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cupo</Label>
              <Input type="number" min="1" value={t.quantity} onChange={(e) => upd(idx, "quantity", e.target.value)} className="rounded-xl border-line h-10 mt-1" data-testid={`ticket-qty-${idx}`} />
            </div>
            <div className="col-span-1 flex justify-end">
              <button onClick={() => rm(idx)} className="text-ink-300 hover:text-coral h-10 flex items-center" aria-label="Quitar" data-testid={`ticket-remove-${idx}`}>
                <Trash2 size={16} strokeWidth={1.75} />
              </button>
            </div>
            <div className="col-span-12">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Descripción</Label>
              <Input value={t.description} onChange={(e) => upd(idx, "description", e.target.value)} placeholder="Incluye cena, mesa reservada…" className="rounded-xl border-line h-10 mt-1" />
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-500 text-sm">Sin tipos de boleto aún.</p>}
      </div>
    </div>
  );
}

function DuesEditor({ value, onChange }) {
  return (
    <div className="pt-4">
      <h2 className="font-heading text-2xl text-ink-900 mb-4">Cuota</h2>
      <div className="bg-white border border-line rounded-2xl p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Monto</Label>
          <Input type="number" min="0" step="0.01" value={value.amount} onChange={(e) => onChange({ ...value, amount: e.target.value })} className="rounded-xl border-line h-11 mt-1" data-testid="dues-amount" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Moneda</Label>
          <Select value={value.currency} onValueChange={(v) => onChange({ ...value, currency: v })}>
            <SelectTrigger className="rounded-xl border-line h-11 mt-1" data-testid="dues-currency"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Fecha límite (opcional)</Label>
          <Input value={value.deadline} onChange={(e) => onChange({ ...value, deadline: e.target.value })} placeholder="30 de septiembre" className="rounded-xl border-line h-11 mt-1" data-testid="dues-deadline" />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Notas / instrucciones</Label>
          <Textarea value={value.notes} onChange={(e) => onChange({ ...value, notes: e.target.value })} placeholder="Cómo pagar, referencias, banco…" className="rounded-xl border-line min-h-[80px] mt-1" data-testid="dues-notes" />
        </div>
        <div className="md:col-span-3 flex items-center gap-2">
          <input type="checkbox" checked={value.allow_partial} onChange={(e) => onChange({ ...value, allow_partial: e.target.checked })} data-testid="dues-partial" />
          <Label className="text-sm text-ink-700">Permitir pagos parciales</Label>
        </div>
      </div>
    </div>
  );
}

function StoreProductsEditor({ items, setItems }) {
  const add = () => setItems([...items, { name: "", price: 0, currency: "MXN", quantity: 20, description: "", image_url: "" }]);
  const upd = (i, f, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [f]: v } : x));
  const rm = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl text-ink-900">Productos</h2>
        <Button variant="outline" onClick={add} className="rounded-full border-line hover:bg-cream-100" data-testid="add-product-btn">
          <Plus size={16} strokeWidth={1.75} className="mr-1" /> Agregar producto
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((p, idx) => (
          <div key={idx} className="bg-white border border-line rounded-2xl p-4 grid grid-cols-12 gap-3 items-end" data-testid={`product-row-${idx}`}>
            <div className="col-span-12 md:col-span-5">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Nombre</Label>
              <Input value={p.name} onChange={(e) => upd(idx, "name", e.target.value)} placeholder="Playera talla M" className="rounded-xl border-line h-10 mt-1" data-testid={`product-name-${idx}`} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Precio</Label>
              <Input type="number" min="0" value={p.price} onChange={(e) => upd(idx, "price", e.target.value)} className="rounded-xl border-line h-10 mt-1" data-testid={`product-price-${idx}`} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Moneda</Label>
              <Select value={p.currency} onValueChange={(v) => upd(idx, "currency", v)}>
                <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid={`product-currency-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Stock (0=ilim.)</Label>
              <Input type="number" min="0" value={p.quantity} onChange={(e) => upd(idx, "quantity", e.target.value)} className="rounded-xl border-line h-10 mt-1" data-testid={`product-qty-${idx}`} />
            </div>
            <div className="col-span-1 flex justify-end">
              <button onClick={() => rm(idx)} className="text-ink-300 hover:text-coral h-10 flex items-center" aria-label="Quitar" data-testid={`product-remove-${idx}`}>
                <Trash2 size={16} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-500 text-sm">Sin productos aún.</p>}
      </div>
    </div>
  );
}

function WaiverEditor({ value, onChange }) {
  return (
    <div className="pt-4">
      <h2 className="font-heading text-2xl text-ink-900 mb-4">Texto del consentimiento</h2>
      <div className="bg-white border border-line rounded-2xl p-6 space-y-4">
        <Textarea
          value={value.waiver_text}
          onChange={(e) => onChange({ ...value, waiver_text: e.target.value })}
          placeholder="Yo, [nombre], declaro que…"
          className="rounded-xl border-line min-h-[220px]"
          data-testid="waiver-text"
        />
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={value.require_parent_signature} onChange={(e) => onChange({ ...value, require_parent_signature: e.target.checked })} data-testid="waiver-parent" />
          <Label className="text-sm text-ink-700">Requiere firma de padre/tutor (si el participante es menor)</Label>
        </div>
      </div>
    </div>
  );
}
